/**
 * Product Alerts section — active alerts + recent history (Timestream-derived).
 */
import * as React from "react";
import { AlertTriangle, Bell } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { ListView } from "../../../ui/components/ListView";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import type { ListParams, ListResult } from "../../../ui/api/listApi.types";
import { useWebSocket } from "../../hooks/useWebSocket";
import { useAuth } from "../../lib/auth/hooks";
import { getApiErrorMessage } from "../../lib/api/api-error";
import {
  dismissProductAlert,
  fetchProductActiveAlerts,
  fetchProductAlertHistory,
  type ProductAlertEpisode,
} from "../../lib/api/products";
import * as subscriptionsApi from "../../lib/api/subscriptions";
import type {
  EventSubscriptionRow,
  ProductAlertPrefs,
  SubscriptionBatchItem,
} from "../../lib/api/subscriptions";
import {
  ALERT_CLOSED_KEY,
  ALERT_OPENED_KEY,
  DEFAULT_ALERT_ENABLED_SEVERITIES,
  cloneAlertPrefs,
  isAlertEventKey,
  isAlertNotifyEnabled,
  moveAlertToList,
  type AlertPrefsDraft,
} from "./productAlertSubscription";
import { ActiveAlertDetailModal } from "./ActiveAlertDetailModal";
import * as alertStyles from "./SimulationAlertControls.css";
import { useI18n } from "../../lib/i18n";
import { alertTypeColor, severityLabel } from "../alerts/alertDisplay";
import { AlertSummaryCard } from "../alerts/AlertSummaryCard";
import { AlertContextPanelContent } from "../alerts/AlertContextPanelContent";
import type { TimelineBarMapping } from "../../../ui/components/ListView/types";
import { AlertInsightPanel } from "./insights/AlertInsightPanel";

export const DEVICE_OFFLINE_MESSAGE =
  "The device appears to be offline or is not reporting data";

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function useElapsedSince(startIso: string | null | undefined): string {
  const [display, setDisplay] = React.useState("—");
  React.useEffect(() => {
    if (!startIso) {
      setDisplay("—");
      return;
    }
    const update = () => {
      const elapsed = Date.now() - new Date(startIso).getTime();
      setDisplay(formatDuration(Math.max(0, elapsed)));
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [startIso]);
  return display;
}

function defaultAlertPrefs(): AlertPrefsDraft {
  return {
    enabledSeverities: [...DEFAULT_ALERT_ENABLED_SEVERITIES],
    overrides: [],
  };
}

function ActiveAlertTile({
  alert,
  subscribed,
  onOpen,
}: {
  alert: ProductAlertEpisode;
  subscribed: boolean;
  onOpen: () => void;
}) {
  const { t } = useI18n();
  const duration = useElapsedSince(alert.openedAt);
  const severity = Math.min(5, Math.max(1, alert.severity ?? 3));
  const severityText = severityLabel(alert.severity);

  return (
    <button
      type="button"
      className={alertStyles.activeAlertTile}
      data-severity={String(severity)}
      title={[
        alert.name,
        `Code: ${alert.code}`,
        `Type: ${alert.type === "fault" ? "Fault" : "Alarm"}`,
        `Severity: ${severityText}`,
        `Open for: ${duration}`,
        subscribed ? "Subscribed to notifications" : null,
      ]
        .filter(Boolean)
        .join("\n")}
      onClick={onOpen}
    >
      <span className={alertStyles.activeAlertTileIcon}>
        <span className={alertStyles.activeAlertTileRing} aria-hidden />
        {alert.type === "fault" ? (
          <AlertTriangle size={24} strokeWidth={2.25} aria-hidden />
        ) : (
          <Bell size={24} strokeWidth={2.25} aria-hidden />
        )}
      </span>
      <span className={alertStyles.activeAlertTileName}>{alert.name}</span>
      <span className={alertStyles.activeAlertTileCode}>
        <span className={alertStyles.activeAlertTileCodeText}>{alert.code}</span>
        {subscribed ? (
          <span className={alertStyles.activeAlertSubscribedBadge}>{t("pro.products.alerts.subscribed")}</span>
        ) : null}
      </span>
      <span className={alertStyles.activeAlertTileDuration} aria-label={`Open for ${duration}`}>
        {duration}
      </span>
    </button>
  );
}

type AlertRow = ProductAlertEpisode & { id: string };

export function ProductAlertsSection({
  productId,
  productName,
  owningOrganizationId,
  token,
  canDismissAlerts = false,
  offline = false,
  onCountsChange,
  insightsEnabled = false,
  showInsightUsage = false,
}: {
  productId: string;
  productName: string;
  owningOrganizationId?: string | null;
  token: string | null;
  /** Whether the current user has edit-or-above access to this product. */
  canDismissAlerts?: boolean;
  /** When true, skip fetch and show unified offline messaging. */
  offline?: boolean;
  onCountsChange?: (counts: { active: number; recent: number }) => void;
  /** Show the AI explanation panel in the alert detail modal (Insights). */
  insightsEnabled?: boolean;
  showInsightUsage?: boolean;
}) {
  const { user } = useAuth();
  const { t } = useI18n();
  const subscriberUserId = user?.id ?? "";

  const [active, setActive] = React.useState<ProductAlertEpisode[]>([]);
  const [history, setHistory] = React.useState<ProductAlertEpisode[]>([]);
  const [loading, setLoading] = React.useState(!offline);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const { subscribe, unsubscribe } = useWebSocket();

  const [subscriptions, setSubscriptions] = React.useState<EventSubscriptionRow[]>([]);
  const [alertPrefs, setAlertPrefs] = React.useState<AlertPrefsDraft | null>(null);
  const [hasAlertEvents, setHasAlertEvents] = React.useState(false);
  const [subsLoading, setSubsLoading] = React.useState(false);
  const [selectedAlert, setSelectedAlert] = React.useState<ProductAlertEpisode | null>(null);
  const [detailOpen, setDetailOpen] = React.useState(false);
  /** Bumped on every reload() (mount + WS push) — passed to the Timeline's
   *  ListView as `refreshToken` so it refetches its own (possibly differently
   *  ranged) query on live updates too, without resetting the user's selected
   *  date range the way `contextKey` would (contextKey also resets filters). */
  const [dataGen, setDataGen] = React.useState(() => Date.now());
  const [selectedTimelineId, setSelectedTimelineId] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    if (!token || offline) return;
    setLoadFailed(false);
    const end = new Date();
    const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
    const [activeRes, histRes] = await Promise.all([
      fetchProductActiveAlerts(token, productId),
      fetchProductAlertHistory(token, productId, {
        start: start.toISOString(),
        end: end.toISOString(),
      }),
    ]);
    setActive((prev) => {
      const prevBy = new Map(prev.map((a) => [a.code.toUpperCase(), a]));
      return (activeRes.data ?? []).map((a) => {
        const old = prevBy.get(a.code.toUpperCase());
        if (!old?.openedAt || !a.openedAt) return a;
        const oldT = new Date(old.openedAt).getTime();
        const newT = new Date(a.openedAt).getTime();
        if (Number.isFinite(oldT) && Number.isFinite(newT) && oldT < newT) {
          return { ...a, openedAt: old.openedAt };
        }
        return a;
      });
    });
    setHistory(histRes.data ?? []);
    setDataGen((g) => g + 1);
  }, [token, productId, offline]);

  const reloadSubscriptions = React.useCallback(async () => {
    if (!token || !subscriberUserId || offline) {
      setSubscriptions([]);
      setAlertPrefs(null);
      setHasAlertEvents(false);
      return;
    }
    setSubsLoading(true);
    try {
      const result = await subscriptionsApi.fetchSubscriptions(token, {
        subscriberUserId,
        entityType: "product",
        entityId: productId,
      });
      const rows = result.subscriptions ?? [];
      const alertSubscribed = rows.some((r) => isAlertEventKey(r.systemEventKey));
      setSubscriptions(rows);
      setHasAlertEvents(alertSubscribed);
      setAlertPrefs(
        alertSubscribed
          ? cloneAlertPrefs(result.alertPrefs ?? defaultAlertPrefs())
          : null,
      );
    } catch {
      setSubscriptions([]);
      setAlertPrefs(null);
      setHasAlertEvents(false);
    } finally {
      setSubsLoading(false);
    }
  }, [token, subscriberUserId, productId, offline]);

  React.useEffect(() => {
    if (offline) {
      onCountsChange?.({ active: 0, recent: 0 });
    }
  }, [offline, onCountsChange]);

  React.useEffect(() => {
    onCountsChange?.({ active: active.length, recent: history.length });
  }, [active.length, history.length, onCountsChange]);

  React.useEffect(() => {
    if (offline) {
      setLoading(false);
      setActive([]);
      setHistory([]);
      return;
    }
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void reload()
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, productId, reload, refreshKey, offline]);

  React.useEffect(() => {
    if (offline || !productId) return;
    const topic = `telemetry:products:${productId}`;
    const handler = () => {
      void reload();
    };
    subscribe([topic], handler);
    return () => unsubscribe([topic], handler);
  }, [productId, offline, subscribe, unsubscribe, reload]);

  React.useEffect(() => {
    void reloadSubscriptions();
  }, [reloadSubscriptions]);

  React.useEffect(() => {
    if (!detailOpen || !selectedAlert) return;
    const stillActive = active.some(
      (a) => a.code.toUpperCase() === selectedAlert.code.toUpperCase(),
    );
    if (!stillActive) {
      setDetailOpen(false);
      setSelectedAlert(null);
    }
  }, [active, detailOpen, selectedAlert]);

  const isSubscribedToAlert = React.useCallback(
    (alert: ProductAlertEpisode) => {
      if (!hasAlertEvents || !alertPrefs) return false;
      return isAlertNotifyEnabled(
        alertPrefs,
        alert.code,
        Math.min(5, Math.max(1, alert.severity ?? 3)),
      );
    },
    [hasAlertEvents, alertPrefs],
  );

  const handleToggleSubscription = React.useCallback(async () => {
    if (!token || !subscriberUserId || !selectedAlert) {
      throw new Error("Unable to update alert notifications");
    }
    const severity = Math.min(5, Math.max(1, selectedAlert.severity ?? 3));
    const currentlySubscribed = isSubscribedToAlert(selectedAlert);
    const target: "allowed" | "blocked" = currentlySubscribed ? "blocked" : "allowed";

    const existing = subscriptions;
    const alertRows = existing.filter((r) => isAlertEventKey(r.systemEventKey));
    let nextPrefs: AlertPrefsDraft = cloneAlertPrefs(
      alertPrefs ?? defaultAlertPrefs(),
    );
    nextPrefs = moveAlertToList(nextPrefs, selectedAlert.code, severity, target);

    const batch: SubscriptionBatchItem[] = existing
      .filter((r) => !isAlertEventKey(r.systemEventKey))
      .map((r) => ({
        systemEventKey: r.systemEventKey,
        notifyEmail: r.notifyEmail,
        notifySms: r.notifySms,
        notifyWeb: r.notifyWeb,
        notifyPush: r.notifyPush ?? false,
        minSeverity: r.minSeverity,
      }));

    if (alertRows.length > 0) {
      for (const r of alertRows) {
        batch.push({
          systemEventKey: r.systemEventKey,
          notifyEmail: r.notifyEmail,
          notifySms: r.notifySms,
          notifyWeb: r.notifyWeb,
          notifyPush: r.notifyPush ?? false,
          minSeverity: "all",
        });
      }
    } else if (target === "allowed") {
      const seed = existing[0];
      for (const key of [ALERT_OPENED_KEY, ALERT_CLOSED_KEY]) {
        batch.push({
          systemEventKey: key,
          notifyEmail: seed?.notifyEmail ?? true,
          notifySms: seed?.notifySms ?? false,
          notifyWeb: seed?.notifyWeb ?? true,
          notifyPush: seed?.notifyPush ?? false,
          minSeverity: "all",
        });
      }
    }

    const prefsPayload: ProductAlertPrefs | null =
      batch.some((b) => isAlertEventKey(b.systemEventKey))
        ? cloneAlertPrefs(nextPrefs)
        : null;

    try {
      const saved = await subscriptionsApi.saveSubscriptionsBatch(token, {
        subscriberUserId,
        entityType: "product",
        entityId: productId,
        owningOrganizationId,
        entityLabel: productName,
        subscriptions: batch,
        alertPrefs: prefsPayload,
      });
      const rows = saved.subscriptions ?? [];
      const alertSubscribed = rows.some((r) => isAlertEventKey(r.systemEventKey));
      setSubscriptions(rows);
      setHasAlertEvents(alertSubscribed);
      setAlertPrefs(
        alertSubscribed
          ? cloneAlertPrefs(saved.alertPrefs ?? nextPrefs)
          : null,
      );
    } catch (err) {
      throw new Error(
        getApiErrorMessage(err, "Failed to update alert notifications"),
      );
    }
  }, [
    token,
    subscriberUserId,
    selectedAlert,
    isSubscribedToAlert,
    subscriptions,
    alertPrefs,
    productId,
    owningOrganizationId,
    productName,
  ]);

  const handleDismissAlert = React.useCallback(
    async (incidentId: string, reason: string) => {
      if (!token) throw new Error("Not authenticated");
      await dismissProductAlert(token, productId, incidentId, reason);
      await reload();
    },
    [token, productId, reload],
  );

  const historyColumns = React.useMemo<ColumnDefinition<AlertRow>[]>(
    () => [
      { id: "code", label: "Code", render: (r) => <span>{r.code}</span> },
      { id: "name", label: "Name", render: (r) => <span>{r.name}</span> },
      {
        id: "type",
        label: "Type",
        render: (r) => <span>{r.type ?? "—"}</span>,
      },
      {
        id: "severity",
        label: "Severity",
        render: (r) => (
          <span>{severityLabel(r.severity)}</span>
        ),
      },
      {
        id: "openedAt",
        label: "Opened",
        render: (r) => <span>{formatWhen(r.openedAt)}</span>,
      },
      {
        id: "closedAt",
        label: "Closed",
        render: (r) => <span>{r.active ? "Open" : formatWhen(r.closedAt)}</span>,
      },
    ],
    [],
  );

  const timelineMapping = React.useMemo<TimelineBarMapping<AlertRow>>(
    () => ({
      getStart: (r) => r.openedAt,
      getEnd: (r) => (r.active ? null : r.closedAt),
      getLabel: (r) => r.name,
      getColor: (r) => alertTypeColor(r.type),
      // No getRowKey — each incident gets its own row (the default), so
      // overlapping incidents never draw on top of one another.
    }),
    [],
  );

  // Default Timeline range: today, local midnight to next midnight — same
  // convention (and the same Date Range filter chip) as Historical Data.
  const todayRangeFilter = React.useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return [{ id: "__timeRange", value: [start.toISOString(), end.toISOString()] }];
  }, []);

  const fetchHistoryList = React.useCallback(
    async (params: ListParams): Promise<ListResult<AlertRow>> => {
      if (!token) return { data: [], total: 0 };
      const start = (params.filters?.timeRangeStart as string | undefined) ?? todayRangeFilter[0]!.value[0]!;
      const end = (params.filters?.timeRangeEnd as string | undefined) ?? todayRangeFilter[0]!.value[1]!;
      const res = await fetchProductAlertHistory(token, productId, { start, end });
      const q = (params.search ?? "").toLowerCase().trim();
      let rows = (res.data ?? []).map((r, i) => ({
        ...r,
        id: `${r.code}-${r.openedAt}-${i}`,
      }));
      if (q) {
        rows = rows.filter(
          (r) =>
            r.code.toLowerCase().includes(q) ||
            r.name.toLowerCase().includes(q) ||
            (r.type ?? "").toLowerCase().includes(q),
        );
      }
      return { data: rows, total: rows.length };
    },
    [token, productId, todayRangeFilter],
  );

  if (offline || loadFailed) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 0" }}>
        <Text level="body-small" style={{ color: "#9CA3AF", textAlign: "center" }}>
          {DEVICE_OFFLINE_MESSAGE}
        </Text>
      </div>
    );
  }

  if (loading) {
    return (
      <Text level="body-small" style={{ color: "var(--color-fgMuted)" }}>
        Loading alerts…
      </Text>
    );
  }

  const selectedSubscribed = selectedAlert
    ? isSubscribedToAlert(selectedAlert)
    : false;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <Text level="h3" weight="bold" style={{ marginBottom: 10, display: "block" }}>
          {t("pro.products.alerts.activeTitle")}
        </Text>
        {active.length === 0 ? (
          <Text
            level="body-small"
            style={{ color: "var(--color-fgMuted)", textAlign: "center", display: "block" }}
          >
            No active alerts.
          </Text>
        ) : (
          <div className={alertStyles.activeAlertGrid} aria-label={t("pro.products.alerts.activeTitle")}>
            {active.map((a) => (
              <ActiveAlertTile
                key={a.code}
                alert={a}
                subscribed={isSubscribedToAlert(a)}
                onOpen={() => {
                  setSelectedAlert(a);
                  setDetailOpen(true);
                }}
              />
            ))}
          </div>
        )}
      </div>

      <div>
        <Text level="h3" weight="bold" style={{ marginBottom: 10, display: "block" }}>
          Incident Timeline
        </Text>
        <ListView<AlertRow>
          listId={`product-alerts-history-${productId}`}
          refreshToken={dataGen}
          columns={historyColumns}
          fetchList={fetchHistoryList}
          enabledViews={["timeline"]}
          disableLocalPersistence
          compact
          searchPlaceholder="Search by code or name…"
          emptyTitle="No alert history"
          emptySubtitle={t("pro.products.alerts.recentEmptySubtitle")}
          initialColumnFilters={todayRangeFilter}
          timeRangeFilter={{ enabled: true }}
          timelineMapping={timelineMapping}
          renderTimelineHoverCard={(rows, _cursorTimeMs, hoveredId, selectedTimelineAlertId) => (
            <AlertSummaryCard
              alerts={rows}
              getColor={(a) => alertTypeColor(a.type)}
              highlightedId={hoveredId}
              selectedId={selectedTimelineAlertId}
            />
          )}
          timelineSelectedId={selectedTimelineId}
          onTimelineBarSelect={(row) => setSelectedTimelineId(row?.id ?? null)}
          renderTimelineAlertContextPanel={(row) => (
            <AlertContextPanelContent
              alert={row}
              canDismiss={canDismissAlerts}
              onDismiss={(reason) => handleDismissAlert(row.incidentId, reason)}
            />
          )}
        />
      </div>

      <ActiveAlertDetailModal
        open={detailOpen}
        setOpen={(next) => {
          setDetailOpen(next);
          if (!next) setSelectedAlert(null);
        }}
        alert={selectedAlert}
        subscribed={selectedSubscribed}
        canManageSubscription={Boolean(subscriberUserId)}
        subscriptionLoading={subsLoading}
        onToggleSubscription={handleToggleSubscription}
        explanation={
          insightsEnabled && selectedAlert ? (
            <AlertInsightPanel
              productId={productId}
              alertCode={selectedAlert.code}
              alertName={selectedAlert.name}
              token={token}
              showUsage={showInsightUsage}
            />
          ) : undefined
        }
      />
    </div>
  );
}
